import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { notification } from "antd";
import { useIntl } from "react-intl";

import { PermissionName } from "@mapmycustomers/shared";
import { EntityType, Person } from "@mapmycustomers/shared/types/entity";
import Company from "@mapmycustomers/shared/types/entity/Company";
import { PlatformFilterCondition } from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Button, Modal } from "@mapmycustomers/ui";

import noRecordsImageMap from "@app/component/associations/utils/noRecordsImages";
import CreatePersonModal from "@app/component/createEditEntity/CreatePersonModal";
import { getSourceObjectValues, hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useDefaultSourceObjectMessageArgs from "@app/util/hook/useDefaultSourceObjectMessageArgs";
import { useRecordLimitCheckWith } from "@app/util/hook/useRecordLimitCheck";
import { getSourceObjectMessageArgs } from "@app/util/ui";

import Associations from "../components/Associations";
import {
  filterPeople,
  getFilteredPeopleCount,
  getPeople,
  getTotalPeople,
  isLoading,
} from "../store";
import useEntitySorter from "../utils/useEntitySorter";

import messages from "./messages";
import styles from "./PeopleAssociation.module.scss";

interface PeopleAssociationProps {
  allowAdd?: boolean;
  assignedPeople?: Person[];
  associateWith?: Person["id"];
  defaultQuery?: string;
  disableNonEditableEntities?: boolean;
  fixedCompany?: Company;
  getListTitle?: (count: number, total: number) => string;
  hideOnSelect?: boolean;
  info?: React.ReactNode;
  maxCount?: number;
  multiselect?: boolean;
  okLoading?: boolean;
  okText?: string;
  onHide: (updated?: boolean) => void;
  onPersonCreated?: (newPerson: Person) => void;
  onSelect?: (selectedPeopleIds: Person["id"][], removedPeopleIds?: Person["id"][]) => void;
  onUpdatePrimaryPerson?: (contactId?: Person["id"]) => void;
  primaryPersonId?: Person["id"];
  requestFilters?: PlatformFilterCondition[];
  suggestedPeople?: Person[];
  supportsPrimaryEntity?: boolean;
  title?: string;
  zIndex?: number;
}

const PeopleAssociation = ({
  allowAdd = true,
  assignedPeople,
  associateWith,
  canCreatePeople,
  defaultQuery = "",
  disableNonEditableEntities,
  filtered,
  fixedCompany,
  getListTitle,
  hideOnSelect = true,
  info,
  loading,
  maxCount,
  multiselect = true,
  okLoading,
  okText,
  onHide,
  onPersonCreated,
  onSearch,
  onSelect,
  onUpdatePrimaryPerson,
  people,
  primaryPersonId,
  requestFilters,
  sourceObjectValues,
  suggestedPeople,
  supportsPrimaryEntity,
  title,
  total,
  zIndex,
}: PeopleAssociationProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const defaultTypeArgs = useDefaultSourceObjectMessageArgs(EntityType.PERSON);
  // The record the list shows for itself names its own type
  const cantAssociateWithSelfMessage = useCallback(
    (person: Person) =>
      intl.formatMessage(
        messages.cantAssociateWithSelf,
        getSourceObjectMessageArgs(intl, EntityType.PERSON, sourceObjectValues, person.sourceObject)
      ),
    [intl, sourceObjectValues]
  );

  const [internalPrimaryPersonId] = useState<Person["id"] | undefined>(primaryPersonId);

  // Person object has companies property which is used to filter all the already associated people based on fixedCompany
  const assignedList = useMemo(() => {
    return assignedPeople
      ? assignedPeople
      : people && fixedCompany && multiselect
        ? people.filter((person) => person.accounts.some(({ id }) => id === fixedCompany.id))
        : [];
  }, [assignedPeople, fixedCompany, multiselect, people]);

  const [query, setQuery] = useState<string>(defaultQuery);
  const [newSelectedPeopleIds, setNewSelectedPeopleIds] = useState<Set<Person["id"]>>(
    () => new Set()
  );
  const [removedPeopleIds, setRemovedPeopleIds] = useState<Set<Person["id"]>>(() => new Set());

  const [createdPeople, setCreatedPeople] = useState<Person[]>([]);
  const sortedPeople = useEntitySorter(
    people ?? [],
    createdPeople,
    query.trim().length > 0,
    assignedList,
    suggestedPeople,
    primaryPersonId
  );

  const assignedPeopleIds = useMemo(
    () => new Set(assignedList.map(({ id }) => id)),
    [assignedList]
  );

  const isPersonSelected = useCallback(
    (id: Person["id"]) => {
      const wasInitiallySelectedAndNotRemoved =
        assignedPeopleIds.has(id) && !removedPeopleIds.has(id);

      const wasNewlySelected = newSelectedPeopleIds.has(id);

      return wasInitiallySelectedAndNotRemoved || wasNewlySelected;
    },
    [assignedPeopleIds, newSelectedPeopleIds, removedPeopleIds]
  );

  const visiblePeopleIds = useMemo(() => sortedPeople.map(({ id }) => id), [sortedPeople]);

  const selectedPeopleIds = useMemo(
    () => visiblePeopleIds.filter((id) => isPersonSelected(id)),
    [isPersonSelected, visiblePeopleIds]
  );

  // Real total on submit (assigned minus removed, plus newly selected); `selectedPeopleIds` undercounts under a search filter.
  const resultingPeopleIds = new Set(newSelectedPeopleIds);
  for (const id of assignedPeopleIds) {
    if (!removedPeopleIds.has(id)) {
      resultingPeopleIds.add(id);
    }
  }
  const resultingPeopleCount = resultingPeopleIds.size;

  useEffect(() => {
    onSearch({ query: defaultQuery, requestFilters });
  }, [defaultQuery, onSearch, requestFilters]);

  const handleSearch = useCallback(
    (query: string) => onSearch({ query, requestFilters }),
    [onSearch, requestFilters]
  );

  const [createNewPersonVisible, showCreatePersonModal, hideCreatePersonModal] = useBoolean();
  const handleHideCreatePersonModal = useDynamicCallback((newPerson?: Person) => {
    const alreadyAssignedPeopleIds = new Set(
      [...assignedList, ...createdPeople].map(({ id }) => id)
    );
    const hadAnySelectedPeopleAlready = selectedPeopleIds.some(
      (id) => !alreadyAssignedPeopleIds.has(id)
    );
    if (newPerson) {
      if (!fixedCompany && !onPersonCreated) {
        setNewSelectedPeopleIds(
          (ids) => new Set(multiselect ? [newPerson.id, ...ids] : [newPerson.id])
        );
      }
      // we also store created people, just to show then in the top of the list, to not confuse user
      // because otherwise, newly added record could get filtered out according to current filter
      // that's also the reason why we reset the filter here
      setCreatedPeople((people) => [...people, newPerson]);
      setQuery("");
      onSearch({ query: "", requestFilters });
      onPersonCreated?.(newPerson);
    }
    hideCreatePersonModal();

    // if user selected no people in the modal before adding new person, then just hide both modals
    if (!hadAnySelectedPeopleAlready && newPerson) {
      onSelect?.([newPerson.id], Array.from(removedPeopleIds));
      if (hideOnSelect) {
        onHide(true);
      }
    }
  });

  const hasChanges = multiselect
    ? newSelectedPeopleIds.size > 0 || removedPeopleIds.size > 0
    : newSelectedPeopleIds.size > 0;

  const alreadyAssignedEntities = useMemo(
    () => [...assignedList, ...createdPeople],
    [assignedList, createdPeople]
  );

  const handleRowsChange = useCallback(
    (selectedRows: Person["id"][]) => {
      const currentlySelected = new Set(selectedRows);

      const updatedNewSelectedPeopleIds = new Set(newSelectedPeopleIds);
      const updatedRemovedPeopleIds = new Set(removedPeopleIds);

      for (const id of visiblePeopleIds) {
        const wasInitiallyAssigned = assignedPeopleIds.has(id);
        const isNowSelected = currentlySelected.has(id);

        if (isNowSelected) {
          if (wasInitiallyAssigned) {
            updatedRemovedPeopleIds.delete(id);
          } else {
            updatedNewSelectedPeopleIds.add(id);
          }
        } else {
          if (wasInitiallyAssigned) {
            updatedRemovedPeopleIds.add(id);
          } else {
            updatedNewSelectedPeopleIds.delete(id);
          }
        }
      }

      setNewSelectedPeopleIds(updatedNewSelectedPeopleIds);
      setRemovedPeopleIds(updatedRemovedPeopleIds);
    },
    [assignedPeopleIds, visiblePeopleIds, newSelectedPeopleIds, removedPeopleIds]
  );

  const modalSubTitle = useMemo(() => {
    const subTitle = [];

    if (newSelectedPeopleIds.size > 0) {
      subTitle.push(
        intl.formatMessage(messages.selectedCount, {
          count: newSelectedPeopleIds.size,
        })
      );
    }
    if (removedPeopleIds.size > 0) {
      subTitle.push(
        intl.formatMessage(messages.deselectCount, {
          count: removedPeopleIds.size,
        })
      );
    }

    return subTitle.join(" • ");
  }, [intl, newSelectedPeopleIds.size, removedPeopleIds.size]);

  const handleOkClick = useCallback(() => {
    if (maxCount !== undefined && resultingPeopleCount > maxCount) {
      notification.error({
        description: intl.formatMessage(messages.associationLimitError, { limit: maxCount }),
        message: intl.formatMessage(messages.associationLimitErrorTitle),
      });
      return;
    }
    onSelect?.(Array.from(newSelectedPeopleIds), Array.from(removedPeopleIds));
    if (hideOnSelect) {
      onHide(true);
    }
  }, [
    hideOnSelect,
    intl,
    maxCount,
    newSelectedPeopleIds,
    onHide,
    onSelect,
    removedPeopleIds,
    resultingPeopleCount,
  ]);

  const handleCancel = useCallback(() => {
    if (internalPrimaryPersonId !== primaryPersonId) {
      onUpdatePrimaryPerson?.(internalPrimaryPersonId);
    }
    onHide(false);
  }, [internalPrimaryPersonId, onHide, onUpdatePrimaryPerson, primaryPersonId]);

  const handleAddClick = useRecordLimitCheckWith(showCreatePersonModal);

  return (
    <Modal
      className={styles.modal}
      okButtonProps={{
        disabled: (!hasChanges && internalPrimaryPersonId === primaryPersonId) || okLoading,
        loading: okLoading,
      }}
      okText={
        okText ??
        intl.formatMessage(messages.footerOkButton, {
          addPeople: assignedList.length === 0,
          multiselect,
        })
      }
      onCancel={handleCancel}
      onOk={handleOkClick}
      open
      title={
        <div className={styles.modalTitle}>
          <span>{title ?? intl.formatMessage(messages.header, { multiselect })}</span>
          {modalSubTitle.length > 0 && <span className={styles.titleSubText}>{modalSubTitle}</span>}
        </div>
      }
      width="clamp(500px, 50vw, 750px)"
      zIndex={zIndex}
    >
      {info}
      <Associations<Person>
        allowUnsetPrimaryEntity={false}
        alreadyAssociatedMessage={intl.formatMessage(messages.alreadyAssociated)}
        assignedRecords={alreadyAssignedEntities}
        associateWith={associateWith}
        cantAssociateWithSelfMessage={cantAssociateWithSelfMessage}
        disableNonEditableEntities={disableNonEditableEntities}
        entities={sortedPeople}
        extras={
          allowAdd && canCreatePeople ? (
            <Button onClick={handleAddClick} type="link">
              {intl.formatMessage(messages.addNewPersonButton, defaultTypeArgs)}
            </Button>
          ) : undefined
        }
        filtered={filtered}
        getListTitle={getListTitle}
        loading={loading}
        multiselect={multiselect}
        noDataImageSrc={noRecordsImageMap[EntityType.PERSON]}
        notEditableMessage={intl.formatMessage(messages.notEditableRowTooltip)}
        onChange={handleRowsChange}
        onChangeQuery={setQuery}
        onSearch={handleSearch}
        onUpdatePrimaryEntity={onUpdatePrimaryPerson}
        placeholder={intl.formatMessage(messages.placeholder)}
        primaryEntityId={primaryPersonId}
        query={query}
        suggestedEntities={suggestedPeople}
        supportsPrimaryEntity={supportsPrimaryEntity}
        total={total}
        value={selectedPeopleIds}
      />
      {createNewPersonVisible && (
        <CreatePersonModal
          fixedCompany={fixedCompany}
          okText={intl.formatMessage(messages.okButton, defaultTypeArgs)}
          onHide={handleHideCreatePersonModal}
        />
      )}
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  canCreatePeople: hasPermission(state)(PermissionName.PERSON_CREATE),
  filtered: getFilteredPeopleCount(state),
  loading: isLoading(state),
  people: getPeople(state),
  sourceObjectValues: getSourceObjectValues(state, EntityType.PERSON),
  total: getTotalPeople(state),
});

const mapDispatchToProps = {
  onSearch: filterPeople.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(PeopleAssociation);
